import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, ButtonTypes } from 'devextreme-react/button';
import { Toast } from 'devextreme-react/toast';
import { LoadPanel } from 'devextreme-react/load-panel';

import { ITableSelectionFormHandle } from '../table-selection/ITableSelectionForm';
import TableSelectionListForm from '../table-selection/TableSelectionListForm';
import TableSelectionTreeviewForm from '../table-selection/TableSelectionTreeviewForm';

import { formatMessage } from "devextreme/localization";

import DataSourceCharacteristicsApi from '../../api/DataSourceCharacteristicsApi';
import TableApi from "./../../api/TableApi";
import FieldApi from "./../../api/FieldApi";

import { JobStatusError } from '../../classes/consts/JobStatus';

import TableSelectionDto from '../../classes/dtos/datastructuremodule/TableSelectionDto';
import DataSourceCharacteristicsDto from '../../classes/dtos/datasourcemodule/DataSourceCharacteristicsDto';

interface TableSelectionProps {
    OriginTableId: number | null;
    OriginPathId: number | null;

    Direction: string;
    DataSourceId: number;
    DatabaseReferenceId: number;
    TableReferenceId: number;
    TableReferenceLabel: string;
    TableTypeLabel: string;
    SetTableSelected(tableSelectionDto: TableSelectionDto): void;
    DataSourceRefreshed(): void;
}

const TableSelection: React.FC<TableSelectionProps> = ({ OriginTableId, OriginPathId, Direction, DataSourceId, DatabaseReferenceId, TableReferenceId, TableReferenceLabel, TableTypeLabel, SetTableSelected, DataSourceRefreshed }) => {

    const position = { of: '#root' };

    const [refreshInProgress, setRefreshInProgress] = useState(false);

    const [dataSourceCharacteristics, setDataSourceCharacteristics] = useState<DataSourceCharacteristicsDto | null>(null);

    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        type: 'info',
        message: '',
        withRedirect: false
    } as {
        isVisible: boolean,
        type: 'info' | 'error' | 'success' | 'warning',
        message: string,
        withRedirect: boolean
    });

    const onHiding = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false,
        });

    }, [toastConfig]);

    const dataSourceCharacteristicsLoad = useCallback(async () => {
        if (DataSourceId === 0) {
            setDataSourceCharacteristics(new DataSourceCharacteristicsDto(false, false, false, false, false, false, false, false));
            return;
        }

        DataSourceCharacteristicsApi.GetDataSourceCharacteristicsByIdAsync(DataSourceId)
            .then(getDataSourceCharacteristicsResult => {
                setDataSourceCharacteristics(getDataSourceCharacteristicsResult.Result);
            });
    }, [DataSourceId]);

    useEffect(() => {
        dataSourceCharacteristicsLoad()
    }, [dataSourceCharacteristicsLoad]);

    const tableSelectionListFormHandleRef = useRef<ITableSelectionFormHandle>(null);
    const tableSelectionTreeviewFormHandleRef = useRef<ITableSelectionFormHandle>(null);

    const handleSetTableSelected = (tableSelectionDto: TableSelectionDto) => {
        SetTableSelected(tableSelectionDto);
    };

    function selectTable(): void {
        if (dataSourceCharacteristics !== null) {
            if (dataSourceCharacteristics.ViewsLinkedToTables) {
                if (tableSelectionTreeviewFormHandleRef.current !== null) {
                    tableSelectionTreeviewFormHandleRef.current.Open(TableReferenceId, Direction === "Target");
                }
            }
            else {
                if (tableSelectionListFormHandleRef.current !== null) {
                    tableSelectionListFormHandleRef.current.Open(TableReferenceId, Direction === "Target");
                }
            }
        }
    };

    const handleRefreshFieldsAsync = async (e: ButtonTypes.ClickEvent) => {
        e.event?.stopPropagation();
        if (TableReferenceId > 0) {
            setRefreshInProgress(true);

            let getTablesResult = await TableApi.GetTablesAsync(DataSourceId, DatabaseReferenceId, true);
            if (getTablesResult.IsFailed()) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: getTablesResult.ErrorMessage,
                    withRedirect: false
                });
                return;
            }

            if (getTablesResult.Result.Accepted === false) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: formatMessage('RefreshTablesNotAccepted'),
                    withRedirect: false
                });
                return;
            }

            if (getTablesResult.Result.Status === JobStatusError) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: formatMessage('RefreshTablesError'),
                    withRedirect: false
                });
                return;
            }

            let getFieldsResult = await FieldApi.GetRawFieldsAsync(DataSourceId, TableReferenceId, true);
            if (getFieldsResult.IsFailed()) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: getFieldsResult.ErrorMessage,
                    withRedirect: false
                });
                return;
            }

            if (getFieldsResult.Result.Accepted === false) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: formatMessage('RefreshFieldsNotAccepted'),
                    withRedirect: false
                });
                return;
            }

            if (getFieldsResult.Result.Status === JobStatusError) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: formatMessage('RefreshFieldsError'),
                    withRedirect: false
                });
                return;
            }

            DataSourceRefreshed();
            setRefreshInProgress(false);
            setToastConfig({
                ...toastConfig,
                isVisible: true,
                type: 'success',
                message: formatMessage('RefreshFieldsSuccess'),
                withRedirect: false
            });
        }
    };

    let forceUpdate = true;
    // TODO : Atente mise � jour des connecteurs let forceUpdate = dataSourceCharacteristics?.ReadSchemaAvailable ?? false;
    let withCreate = dataSourceCharacteristics?.CreateSchemaAvailable ?? false;

    return (
        <React.Fragment>

            {DatabaseReferenceId <= 0 ?
                (<div style={{ width: 440, padding: '20px', opacity: 0.30, background: '#1A1A20', borderRadius: 16, justifyContent: 'flex-start', alignItems: 'center', gap: 32, display: 'inline-flex' }}>
                    <div style={{ flexDirection: 'column', justifyContent: 'left', alignItems: 'left', gap: 12, display: 'inline-flex' }}>
                        <div style={{ alignSelf: 'stretch', color: '#E3E3E8', fontSize: 16, fontFamily: 'Manrope', fontWeight: '600', lineHeight: '24px', letterSpacing: 0.32, wordWrap: 'break-word' }}>3. Choose your table</div>
                        <div style={{ color: '#9393A2', fontSize: 12, fontFamily: 'Manrope', fontWeight: '500', lineHeight: '15.60px', letterSpacing: 0.24, wordWrap: 'break-word' }}>No table selected</div>
                    </div>
                    <img src="svgs/plussource.svg" alt="plus" style={{ marginLeft: 'auto' }} />
                </div>) :
                (<div style={{ width: 440, padding: '20px', background: '#1A1A20', borderRadius: 16, border: '1px #3D3D46 solid', gap: '32px' }}>
                    <div style={{ width: '100%', justifyContent: 'flex-start', alignItems: 'center', display: 'inline-flex' }}>
                        <div style={{ flexDirection: 'column', justifyContent: 'left', alignItems: 'left', gap: 12, display: 'inline-flex' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ color: '#E3E3E8', fontSize: 16, fontFamily: 'Manrope', fontWeight: '600', lineHeight: '24px', letterSpacing: 0.32, wordWrap: 'break-word' }}>3. Choose your {TableTypeLabel}</div>
                                {TableReferenceId > 0 && <img src="svgs/circle.svg" alt="circle" />}
                            </div>
                            <div style={{ color: '#9393A2', fontSize: 12, fontFamily: 'Manrope', fontWeight: '500', lineHeight: '15.60px', letterSpacing: 0.24, wordWrap: 'break-word', alignSelf: 'flex-start' }}>
                                {TableReferenceId > 0 ? TableReferenceLabel + " selected" : "No " + TableTypeLabel + " selected"}
                            </div>
                        </div>
                        {TableReferenceId <= 0 ?
                            <img src="svgs/plussource.svg" alt="plus" style={{ marginLeft: 'auto', cursor: 'pointer' }} onClick={selectTable} /> :
                            <div style={{ marginLeft: 'auto', cursor: 'pointer', gap: 6, display: 'flex', alignItems: 'center' }} onClick={selectTable} >
                            <Button icon="refresh" style={{ marginRight: '5px' }} onClick={handleRefreshFieldsAsync} visible={forceUpdate} />
                                <div style={{ color: '#E3E3E8', fontSize: 14, fontFamily: 'Manrope', fontWeight: '500', lineHeight: '21px', letterSpacing: 0.28, wordWrap: 'break-word' }}>Edit</div>
                                <img src="svgs/edit.svg" alt="circle" />
                            </div>}
                    </div>
                </div>)}
            <TableSelectionListForm ref={tableSelectionListFormHandleRef} OriginTableId={OriginTableId} OriginPathId={OriginPathId} DataSourceId={DataSourceId} DatabaseId={DatabaseReferenceId} WithCreate={withCreate} WithRefresh={forceUpdate} SetTableSelected={handleSetTableSelected} />
            <TableSelectionTreeviewForm ref={tableSelectionTreeviewFormHandleRef} OriginTableId={OriginTableId} OriginPathId={OriginPathId} DataSourceId={DataSourceId} DatabaseId={DatabaseReferenceId} WithCreate={withCreate} WithRefresh={forceUpdate} SetTableSelected={handleSetTableSelected} />
            <LoadPanel
                position={position}
                shadingColor="rgba(0,0,0,0.4)"
                visible={refreshInProgress}
            />
            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                type={toastConfig.type}
                onHiding={onHiding}
                displayTime={toastConfig.type === 'success' ? 1000 : 3000}
            />
        </React.Fragment>
    );
}

export default TableSelection;